import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getSupports, replySupport, supportCloseOpen } from '../../../api/endpoints/support';
import { SUPPORT_TYPE_LABELS, type SupportReply, type SupportTicket } from '../../../api/types/support';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { PageHeader, StatusChip } from '../../../components/common';
import { useSetBreadcrumbLeaf } from '../../../layout/breadcrumbContext';

/** Detail + reply thread + close/reopen. GetSupports is reused with `id` set (see
 *  api/endpoints/support.ts) to load a single ticket — response fields are best-effort
 *  (see api/types/support.ts), so every field below renders defensively. */
export default function SupportDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [replyMessage, setReplyMessage] = useState('');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const query = useQuery({
    queryKey: ['support', 'byId', ticketId],
    queryFn: () => getSupports(ticketId),
    enabled: !!ticketId,
  });
  const ticket: SupportTicket | null = query.data?.supports?.[0] ?? null;
  useSetBreadcrumbLeaf(ticket?.subject || (ticketId ? `Talep #${ticketId}` : null));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['support', 'byId', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['support', 'list'] });
  };

  const replyMutation = useMutation({
    mutationFn: () => replySupport({ supportId: ticketId ?? '', replyMessage }),
    onSuccess: () => {
      enqueueSnackbar('Yanıt gönderildi.', { variant: 'success' });
      setReplyMessage('');
      invalidate();
    },
  });

  const closeOpenMutation = useMutation({
    mutationFn: () => supportCloseOpen({ supportId: ticketId ?? '', isClosed: !ticket?.isClosed }),
    onSuccess: () => {
      enqueueSnackbar(ticket?.isClosed ? 'Talep yeniden açıldı.' : 'Talep kapatıldı.', { variant: 'success' });
      setCloseConfirmOpen(false);
      invalidate();
    },
  });

  return (
    <Box sx={{ maxWidth: 800 }}>
      <PageHeader
        title="Destek Talebi Detayı"
        backTo="/support"
        status={ticket ? <StatusChip tone={ticket.isClosed ? 'neutral' : 'success'} label={ticket.isClosed ? 'Kapalı' : 'Açık'} /> : undefined}
      />

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {ticket && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Box>
                <Typography variant="h6">{ticket.subject || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {ticket.supportType ? SUPPORT_TYPE_LABELS[ticket.supportType] : '—'}
                  {ticket.shopCartID ? ` · Sepet No: ${ticket.shopCartID}` : ''}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setCloseConfirmOpen(true)}
                disabled={closeOpenMutation.isPending}
              >
                {ticket.isClosed ? 'Yeniden Aç' : 'Talebi Kapat'}
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.message || '—'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {ticket && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Yanıtlar
            </Typography>
            {!ticket.replies || ticket.replies.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Henüz yanıt yok.
              </Typography>
            ) : (
              <Stack spacing={2} divider={<Divider />}>
                {ticket.replies.map((reply: SupportReply, idx: number) => (
                  <Box key={reply.id ?? idx}>
                    <Typography variant="caption" color="text.secondary">
                      {reply.isAgencySide ? 'Acente' : 'Destek Ekibi'} {reply.insertedAt ? `· ${reply.insertedAt.slice(0, 16)}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {reply.replyMessage || reply.message || '—'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {ticket && !ticket.isClosed && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Yanıtla
            </Typography>
            <Stack spacing={2}>
              <TextField
                multiline
                minRows={3}
                size="small"
                label="Mesajınız"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
              />
              <ApiErrorAlert error={replyMutation.error} />
              <Box>
                <Button
                  variant="contained"
                  disabled={!replyMessage.trim() || replyMutation.isPending}
                  onClick={() => replyMutation.mutate()}
                >
                  {replyMutation.isPending ? 'Gönderiliyor…' : 'Yanıtı Gönder'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={closeConfirmOpen}
        title={ticket?.isClosed ? 'Talebi yeniden aç' : 'Talebi kapat'}
        description={
          ticket?.isClosed
            ? 'Bu destek talebini yeniden açmak istediğinize emin misiniz?'
            : 'Bu destek talebini kapatmak istediğinize emin misiniz?'
        }
        confirmLabel={ticket?.isClosed ? 'Yeniden Aç' : 'Kapat'}
        loading={closeOpenMutation.isPending}
        onConfirm={() => closeOpenMutation.mutate()}
        onCancel={() => setCloseConfirmOpen(false)}
      />
    </Box>
  );
}
